import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';

// #81 — Settings launcher editor. Curate/re-order the dashboard quick-action tiles anytime.
// Reads the SAME launcher_config the onboarding priorities slot seeds; the catalog comes from the API
// (single source of truth). Chosen tiles show first on the dashboard with a "For You" badge; the rest
// stay available below. Empty selection = the default static layout (no personalization).
export default function LauncherEditor({ fetchWithAuth }) {
  const [catalog, setCatalog] = useState({});
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetchWithAuth(`${API_BASE}/api/dashboard/launcher`);
        if (r.ok && alive) {
          const d = await r.json();
          setCatalog(d.catalog || {});
          setSelected(Array.isArray(d.tile_keys) ? d.tile_keys : []);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchWithAuth]);

  const available = Object.keys(catalog).filter(k => !selected.includes(k));
  const dirty = () => setSaved(false);
  const add = k => { setSelected([...selected, k]); dirty(); };
  const remove = k => { setSelected(selected.filter(x => x !== k)); dirty(); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= selected.length) return;
    const s = [...selected];
    [s[i], s[j]] = [s[j], s[i]];
    setSelected(s); dirty();
  };
  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/dashboard/launcher`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tile_keys: selected, emphasized: selected }),
      });
      if (r.ok) setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="card"><div className="card-body">Loading…</div></div>;

  const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #e4e9f1', borderRadius: 10, marginBottom: 6 };
  const hd = { fontWeight: 700, color: '#16263f', margin: '14px 0 6px' };

  return (
    <div className="card" data-testid="launcher-editor">
      <div className="card-header"><h3 className="card-title">🧭 Dashboard Launcher</h3></div>
      <div className="card-body">
        <p style={{ color: '#6b7f9c', fontSize: 14, marginTop: 0 }}>
          Pick and order the quick actions you want front-and-center on your dashboard. Chosen actions appear
          first with a “For You” badge; the rest stay available below. Leave empty for the default layout.
        </p>

        <div style={hd}>On your dashboard (in order)</div>
        {selected.length === 0 && <div style={{ color: '#8496af', fontSize: 13 }}>None chosen — default layout.</div>}
        {selected.map((k, i) => (
          <div key={k} style={row}>
            <span style={{ fontSize: 18 }}>{catalog[k]?.icon}</span>
            <span style={{ flex: 1, fontWeight: 600, color: '#1a2436' }}>{catalog[k]?.label || k}</span>
            <button className="btn btn-secondary btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
            <button className="btn btn-secondary btn-sm" disabled={i === selected.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
            <button className="btn btn-secondary btn-sm" onClick={() => remove(k)}>Remove</button>
          </div>
        ))}

        {available.length > 0 && (
          <>
            <div style={hd}>Available to add</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {available.map(k => (
                <button key={k} className="btn btn-secondary btn-sm" onClick={() => add(k)}>
                  + {catalog[k]?.icon} {catalog[k]?.label || k}
                </button>
              ))}
            </div>
          </>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save launcher'}</button>
          {saved && <span style={{ color: '#1f7a4d', fontWeight: 600 }}>✓ Saved</span>}
        </div>
      </div>
    </div>
  );
}
